<template>
  <div class="lang-selector" :class="{ 'disabled': disabled }">
    <!-- 触发区域：Icon + 语言文本 -->
    <div 
      class="lang-trigger"
      @click="isOpen = !isOpen"
      :class="{ 'active': isOpen }"
      :disabled="disabled"
    >
      <!-- 语言图标（可替换为项目中的语言标识） -->
    <img src="@/assets/images/icons/icon_yuyan@2x.png" alt="">
      <!-- 当前语言显示 -->
      <!-- <span class="current-lang">
        {{ selectedValue ? (labelKey ? selectedValue[labelKey] : selectedValue) : placeholder }}
      </span> -->
      <!-- 下拉箭头 -->
      <!-- <i class="dropdown-arrow" :class="isOpen ? 'rotated' : ''">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFD200" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </i> -->
    </div>

    <!-- 下拉选项列表 -->
    <div class="lang-options" v-show="isOpen && options.length">
      <div 
        class="lang-option"
        v-for="(option, index) in options"
        :key="index"
        @click="handleSelect(option)"
        :class="{ 'selected': isOptionSelected(option), 'hover': hoverIndex === index }"
        @mouseenter="hoverIndex = index"
        @mouseleave="hoverIndex = -1"
      >
        {{ labelKey ? option[labelKey] : option }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

// 国际化实例
const { locale } = useI18n()

// 组件属性
const props = defineProps({
  options: {
    type: Array,
    default: () => [
      { label: '中文', value: 'zh' },
      { label: 'English', value: 'en' },
      { label: '繁體中文', value: 'zhtw' },
      // { label: 'Tiếng Việt', value: 'yue' }, // 越南语暂时下线
    ]
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  placeholder: {
    type: String,
    default: '选择语言'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  storageKey: {
    type: String,
    default: 'app-language'
  }
})

// 组件事件
const emit = defineEmits(['update:modelValue', 'change', 'lang-switch'])

// 内部状态
const isOpen = ref(false)
const hoverIndex = ref(-1)
const selectedValue = ref(null)

// 初始化选中值
const initSelectedValue = () => {
  const savedLang = localStorage.getItem(props.storageKey)
  if (savedLang) {
    const matchedOption = props.options.find(option => option.value === savedLang)
    if (matchedOption) return matchedOption
  }
  const matchedI18nOption = props.options.find(option => option.value === locale.value)
  if (matchedI18nOption) return matchedI18nOption
  return props.options[0] || null
}
selectedValue.value = initSelectedValue()

// 监听选中值变化
watch(
  () => selectedValue.value,
  (newOption) => {
    if (newOption && newOption.value) {
      locale.value = newOption.value
      localStorage.setItem(props.storageKey, newOption.value)
      emit('update:modelValue', newOption)
      emit('change', newOption)
      emit('lang-switch', { option: newOption, lang: newOption.value })
    }
  },
  { immediate: true }
)

// 监听i18n语言变化
watch(
  () => locale.value,
  (newLang) => {
    const matchedOption = props.options.find(option => option.value === newLang)
    if (matchedOption) {
      selectedValue.value = matchedOption
    }
  }
)

// 检查选项是否选中
const isOptionSelected = (option) => {
  return option.value === selectedValue.value?.value
}

// 选择选项
const handleSelect = (option) => {
  selectedValue.value = option
  isOpen.value = false
  location.reload();
}

// 点击外部关闭
const handleClickOutside = (e) => {
  if (!e.target.closest('.lang-selector')) {
    isOpen.value = false
  }
}
document.addEventListener('click', handleClickOutside)
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped lang="scss">
.lang-selector {
  position: relative;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #fff;

  // 禁用状态
  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  // 触发区域
  .lang-trigger {
    // width: 38px;
    // height: 38px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 8px;
    background-color: #1a1a2e;
    border: 1px solid #2a2a40;
    border-radius: 100%;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(.disabled) {
      // border-color: #FFD200;
    }

    &.active:not(.disabled) {
      // border-color: #FFD200;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    // 正圆图标容器（核心样式）
    .lang-icon-circle {
      width: 28px;
      height: 28px;
      border: 1px solid #FFD200; // 正圆边框颜色
      border-radius: 50%; // 正圆关键属性
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      // hover时边框加粗，增强交互
      .lang-trigger:hover & {
        border-width: 2px;
      }
    }

    // 语言图标
    .lang-icon {
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    // 当前语言文本
    .current-lang {
      white-space: nowrap;
    }

    // 下拉箭头
    .dropdown-arrow {
      transition: transform 0.3s ease;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  // 选项列表
  .lang-options {
    position: absolute;
    top: 100%;
    right: 0;
    width: 160px;
    background-color: #1a1a2e;
    border: 1px solid #2a2a40;
    border-top: none;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    z-index: 999;
    overflow: hidden;
  }

  // 单个选项
  .lang-option {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &.selected {
      background-color: #FFD200;
      color: #1a1a2e;
    }

    &.hover:not(.selected) {
      background-color: #2a2a40;
    }
  }
}
</style>